/**
 * GoLive Service - Multi-platform livestreaming via SRS server
 * Handles WebRTC→WHIP connection and platform management
 */

import { getAuthToken } from './api';

// API Configuration
const LIVESTREAM_API_URL = import.meta.env.VITE_LIVESTREAM_API_URL || 'https://livestream-api.mypodops.com';
const SRS_SERVER_URL = import.meta.env.VITE_SRS_SERVER_URL || 'https://srs.mypodops.com';

// Types
export interface Platform {
  id: string;
  name: string;
  icon: string;
  rtmpUrl: string;
  streamKey: string;
  enabled: boolean;
  status: 'disconnected' | 'connecting' | 'live' | 'error';
  viewerCount?: number;
  errorMessage?: string;
}

export interface LivestreamSession {
  id: number;
  userId: number;
  podcastId?: number;
  status: 'idle' | 'starting' | 'live' | 'stopping' | 'error';
  streamKey: string;
  whipUrl: string;
  isRecording: boolean;
  recordingUrl?: string;
  platforms: Platform[];
  startedAt?: string;
  endedAt?: string;
  totalDurationSeconds?: number;
  peakViewers?: number;
}

export interface StreamStats {
  isLive: boolean;
  duration: number;
  totalViewers: number;
  bitrate: number;
  fps: number;
  resolution: string;
  platformStats: {
    platform: string;
    status: string;
    viewerCount: number;
  }[];
}

export interface GoLiveOptions {
  podcastId?: number;
  platforms: {
    platform: string;
    rtmpUrl: string;
    streamKey: string;
  }[];
  enableRecording?: boolean;
}

// Platform presets
export const PLATFORM_PRESETS: Record<string, { name: string; icon: string; rtmpUrl: string; color: string }> = {
  youtube: {
    name: 'YouTube',
    icon: 'fab fa-youtube',
    rtmpUrl: 'rtmp://a.rtmp.youtube.com/live2',
    color: '#FF0000'
  },
  twitch: {
    name: 'Twitch',
    icon: 'fab fa-twitch',
    rtmpUrl: 'rtmp://live.twitch.tv/app',
    color: '#9146FF'
  },
  facebook: {
    name: 'Facebook',
    icon: 'fab fa-facebook',
    rtmpUrl: 'rtmps://live-api-s.facebook.com:443/rtmp',
    color: '#1877F2'
  },
  linkedin: {
    name: 'LinkedIn',
    icon: 'fab fa-linkedin',
    rtmpUrl: 'rtmps://fa.rtmp.linkedin.com:443/live',
    color: '#0A66C2'
  },
  custom: {
    name: 'Custom RTMP',
    icon: 'fas fa-broadcast-tower',
    rtmpUrl: '',
    color: '#6B7280'
  }
};

// API Helper
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAuthToken();

  const response = await fetch(`${LIVESTREAM_API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new Error(error.message || `HTTP ${response.status}`);
  }

  return response.json();
}

/**
 * Start a livestream session
 */
export async function startLivestream(options: GoLiveOptions): Promise<LivestreamSession> {
  const response = await apiRequest<{ success: boolean; session: LivestreamSession }>(
    '/livestream/start',
    {
      method: 'POST',
      body: JSON.stringify({
        podcastId: options.podcastId,
        platforms: options.platforms,
        enableRecording: options.enableRecording || false,
      }),
    }
  );

  return response.session;
}

/**
 * Stop a livestream session
 */
export async function stopLivestream(sessionId: number): Promise<void> {
  await apiRequest('/livestream/stop', {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  });
}

/**
 * Get current livestream status
 */
export async function getLivestreamStatus(sessionId: number): Promise<LivestreamSession> {
  const response = await apiRequest<{ success: boolean; session: LivestreamSession }>(
    `/livestream/status?sessionId=${sessionId}`
  );
  return response.session;
}

/**
 * Get stream statistics (viewer counts, bitrate, etc.)
 */
export async function getStreamStats(sessionId: number): Promise<StreamStats> {
  const response = await apiRequest<{ success: boolean; stats: StreamStats }>(
    `/livestream/stats?sessionId=${sessionId}`
  );
  return response.stats;
}

/**
 * Add a platform mid-stream
 */
export async function addPlatform(
  sessionId: number,
  platform: { platform: string; rtmpUrl: string; streamKey: string }
): Promise<void> {
  await apiRequest('/livestream/platforms', {
    method: 'POST',
    body: JSON.stringify({ sessionId, ...platform }),
  });
}

/**
 * Remove a platform mid-stream
 */
export async function removePlatform(sessionId: number, platformId: string): Promise<void> {
  await apiRequest(`/livestream/platforms/${platformId}`, {
    method: 'DELETE',
    body: JSON.stringify({ sessionId }),
  });
}

/**
 * Start recording the live stream
 */
export async function startRecording(sessionId: number): Promise<void> {
  await apiRequest('/livestream/record/start', {
    method: 'POST',
    body: JSON.stringify({ sessionId }),
  });
}

/**
 * Stop recording (stream continues)
 */
export async function stopRecording(sessionId: number): Promise<{ recordingUrl: string }> {
  const response = await apiRequest<{ success: boolean; recordingUrl: string }>(
    '/livestream/record/stop',
    {
      method: 'POST',
      body: JSON.stringify({ sessionId }),
    }
  );
  return { recordingUrl: response.recordingUrl };
}

/**
 * Get user's saved platform configurations
 */
export async function getSavedPlatforms(): Promise<Platform[]> {
  const response = await apiRequest<{ success: boolean; platforms: Platform[] }>(
    '/livestream/platforms'
  );
  return response.platforms;
}

/**
 * WebRTC Connection Manager for WHIP
 */
export class WHIPConnection {
  private peerConnection: RTCPeerConnection | null = null;
  private mediaStream: MediaStream | null = null;
  private whipUrl: string;
  private onStatusChange?: (status: string) => void;
  private onError?: (error: Error) => void;

  constructor(
    whipUrl: string,
    callbacks?: {
      onStatusChange?: (status: string) => void;
      onError?: (error: Error) => void;
    }
  ) {
    this.whipUrl = whipUrl;
    this.onStatusChange = callbacks?.onStatusChange;
    this.onError = callbacks?.onError;
  }

  /**
   * Connect to SRS via WHIP with the given media stream
   */
  async connect(stream: MediaStream): Promise<void> {
    this.mediaStream = stream;
    this.onStatusChange?.('connecting');

    try {
      // Create peer connection with STUN servers
      this.peerConnection = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
        ],
      });

      // Add tracks from media stream
      stream.getTracks().forEach((track) => {
        this.peerConnection!.addTrack(track, stream);
      });

      // Handle ICE connection state changes
      this.peerConnection.oniceconnectionstatechange = () => {
        const state = this.peerConnection?.iceConnectionState;
        console.log('ICE connection state:', state);

        if (state === 'connected' || state === 'completed') {
          this.onStatusChange?.('live');
        } else if (state === 'failed' || state === 'disconnected') {
          this.onStatusChange?.('error');
          this.onError?.(new Error(`ICE connection ${state}`));
        }
      };

      // Create offer
      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      // Wait for ICE gathering to complete
      await this.waitForIceGathering();

      // Send offer to SRS via WHIP
      const response = await fetch(this.whipUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/sdp',
        },
        body: this.peerConnection.localDescription?.sdp,
      });

      if (!response.ok) {
        throw new Error(`WHIP request failed: ${response.status}`);
      }

      // Set remote description from SRS answer
      const answerSdp = await response.text();
      await this.peerConnection.setRemoteDescription({
        type: 'answer',
        sdp: answerSdp,
      });

      this.onStatusChange?.('live');
    } catch (error) {
      this.onStatusChange?.('error');
      this.onError?.(error as Error);
      throw error;
    }
  }

  /**
   * Wait for ICE gathering to complete
   */
  private waitForIceGathering(): Promise<void> {
    return new Promise((resolve) => {
      if (this.peerConnection?.iceGatheringState === 'complete') {
        resolve();
        return;
      }

      const checkState = () => {
        if (this.peerConnection?.iceGatheringState === 'complete') {
          this.peerConnection.removeEventListener('icegatheringstatechange', checkState);
          resolve();
        }
      };

      this.peerConnection?.addEventListener('icegatheringstatechange', checkState);

      // Timeout after 10 seconds
      setTimeout(() => {
        this.peerConnection?.removeEventListener('icegatheringstatechange', checkState);
        resolve();
      }, 10000);
    });
  }

  /**
   * Disconnect from SRS
   */
  disconnect(): void {
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }
    this.mediaStream = null;
    this.onStatusChange?.('disconnected');
  }

  /**
   * Get connection statistics
   */
  async getStats(): Promise<RTCStatsReport | null> {
    if (!this.peerConnection) return null;
    return this.peerConnection.getStats();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.peerConnection?.iceConnectionState === 'connected' ||
           this.peerConnection?.iceConnectionState === 'completed';
  }
}

/**
 * Format duration in seconds to HH:MM:SS
 */
export function formatDuration(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m}:${s.toString().padStart(2, '0')}`;
}

/**
 * Format viewer count with K/M suffix
 */
export function formatViewerCount(count: number): string {
  if (count >= 1000000) {
    return (count / 1000000).toFixed(1) + 'M';
  }
  if (count >= 1000) {
    return (count / 1000).toFixed(1) + 'K';
  }
  return count.toString();
}
